import type { VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { Spinner } from '#components/spinner/spinner.tsx';
import { cn } from '#lib/utils.ts';
import { cva } from 'class-variance-authority';

import { buttonFocusRingClasses } from './constants.ts';

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-all disabled:pointer-events-none disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 active:opacity-60 aria-invalid:border-destructive  hover:cursor-pointer",
    buttonFocusRingClasses,
  ],
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 aria-disabled:hover:bg-primary',
        secondary:
          'border shadow-xs bg-background text-dark hover:bg-component-hover aria-disabled:hover:bg-background',
        destructive: 'bg-destructive text-light shadow-xs hover:bg-destructive/90 aria-disabled:hover:bg-destructive',
        ghost: 'hover:bg-component-hover aria-disabled:hover:bg-transparent',
        link: 'cursor-pointer text-primary underline-offset-4 hover:underline aria-disabled:hover:no-underline',
        'inline-link':
          'cursor-pointer text-primary underline-offset-4 hover:underline aria-disabled:hover:no-underline',
      },
      size: {
        default: 'text-sm h-10 px-4 py-2 has-[>svg]:px-3',
        sm: 'text-xs h-8 gap-1.5 px-3 has-[>svg]:px-2.5',
        xs: 'text-xs h-5 gap-1 px-1.5 has-[>svg]:px-1.5',
        // The default shadcn combobox requests icon-xs
        'icon-xs':
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
      },
      iconPosition: {
        left: 'flex-row',
        right: 'flex-row-reverse',
      },
    },
    compoundVariants: [{ variant: 'inline-link', className: 'h-auto p-0 has-[>svg]:px-0' }],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      iconPosition: 'left',
    },
  },
);

export type ButtonProps = Omit<ButtonPrimitive.Props, 'className'> &
  VariantProps<typeof buttonVariants> & {
    className?: string;
    isLoading?: boolean;
    icon?: ReactNode;
    iconPosition?: 'left' | 'right';
  };

function Button({
  className,
  variant,
  size,
  iconPosition = 'left',
  isLoading = false,
  disabled = false,
  icon,
  children,
  ...props
}: ButtonProps) {
  return (
    <ButtonPrimitive
      data-slot="button"
      disabled={disabled || isLoading}
      className={cn(buttonVariants({ variant, size, iconPosition }), className)}
      {...props}
    >
      {isLoading && <Spinner className="text-disabled" />}
      {!isLoading && icon}
      {children}
    </ButtonPrimitive>
  );
}

export { Button, buttonVariants };
